import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ImageUp, RotateCcw, X } from "lucide-react";
import { ClubCrest } from "@/components/ClubMark";
import { getUploadedClubLogoUrl } from "@/lib/matchLogos";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  ApiError,
  getClubSettings,
  getHostClubAdmins,
  getHostClubAdminCandidates,
  grantHostClubAdmin,
  inviteHostClubAdmin,
  revokeHostClubAdmin,
  updateClubSettings,
  uploadClubSettingsAsset,
  type ClubSettingsPayload,
} from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { publicAppSettingsKey, unscopedKey } from "@/lib/queryClient";
import { coerceBool } from "@/lib/coerce";
import { useMe } from "@/features/auth/useMe";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import type { UpdateClubSettingsRequest } from "@/lib/types";
import { deriveBrandColorTokens } from "@/features/navigation/brandColorTokens";

/**
 * `/admin/settings` — the CLUB admin settings page (Decision K; originally
 * M8c H1 / H3 / H4 / H6 / H7).
 *
 * Club identity (title + intro) + Downloads toggle + onboarding flags +
 * Asset uploads (logo / favicon) all speak the club endpoints
 * (`GET/PUT /api/club/settings`, `POST /api/club/settings/asset`), scoped to
 * the HOST club. The Email/Notification, Auth-provider, and settings-audit
 * cards are system-wide infra: they render on the super-admin console
 * (/super-admin/system) and their definitions live in
 * features/super-admin/pages/system-cards.tsx.
 *
 * **H2 build-time decision (locked):** the three live-streaming
 * fields (`live_enabled`, `live_rtmp_public_url`,
 * `live_offline_message`) are NOT rendered here. A one-line
 * pointer directs operators to `/admin/live`. This avoids the
 * legacy bug where a partial Settings save could clobber Live
 * config — and removes a confusing dual-edit surface.
 *
 * Save flow (H6): partial PUT — only changed keys go to the backend
 * (`PUT /api/club/settings` returns `{club_id, settings, assets}`; no audit
 * echo). We invalidate the club payload + the public app-settings cache so
 * the shell/header picks branding changes up immediately.
 */

type BrandingSettingKey = "club_title" | "season_intro";

/** Operator-facing club identity fields. */
const BRANDING_FIELDS: Array<{
  key: BrandingSettingKey;
  label: string;
  group: "club";
  kind?: "text" | "textarea";
}> = [
  { key: "club_title", label: "Club title", group: "club" },
  { key: "season_intro", label: "Club intro", group: "club", kind: "textarea" },
];

type OnboardingSettingKey =
  | "onboarding_self_service_enabled"
  | "onboarding_social_login_enabled"
  | "onboarding_google_enabled"
  | "onboarding_apple_enabled";

type BrandColourField = "primary" | "secondary";

const BRAND_COLOUR_PRESETS = [
  { name: "Royal blue", hex: "#2563EB" },
  { name: "Sky blue", hex: "#0284C7" },
  { name: "Emerald", hex: "#059669" },
  { name: "Forest", hex: "#166534" },
  { name: "Crimson", hex: "#DC2626" },
  { name: "Burgundy", hex: "#881337" },
  { name: "Amber", hex: "#D97706" },
  { name: "Orange", hex: "#EA580C" },
  { name: "Violet", hex: "#7C3AED" },
  { name: "Charcoal", hex: "#111827" },
] as const;

const BRAND_HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export function AdminSettingsPage() {
  // The club's own branding/flags (Decision K: per-club settings live behind
  // /api/club/settings, club-admin gated on the host club).
  const settingsQuery = useQuery({
    queryKey: unscopedKey("club-settings"),
    queryFn: getClubSettings,
  });
  // Co-admin delegation is a club-admin surface (super admins acting on the
  // club host resolve to club_admin caps too). Plain members never see it.
  const me = useMe();
  const canManageAdmins = Boolean(
    me.data?.is_club_admin || me.data?.is_super_admin,
  );

  return (
    <div data-testid="admin-settings" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Club"
        title="Profile & Brand"
        description="The identity your members see, and the way people join."
        meta={settingsQuery.isFetching ? "Refreshing…" : undefined}
      />

      {settingsQuery.error ? (
        <p
          data-testid="admin-settings-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load settings.
        </p>
      ) : null}

      {settingsQuery.data ? (
        <div className="admin-profile-brand-layout">
          <section
            id="settings-brand"
            data-testid="admin-settings-section-brand"
            className="admin-profile-brand-card"
          >
            <header className="admin-profile-brand-heading">
              <div>
                <p className="eyebrow">Brand &amp; public experience</p>
                <h2>Make the club recognizable</h2>
              </div>
              <span>{settingsQuery.isFetching ? "Refreshing…" : "Saved"}</span>
            </header>
            <BrandingCard settings={settingsQuery.data} />
            <ClubColoursCard
              settings={settingsQuery.data}
              assetsSlot={<AssetsCard settings={settingsQuery.data} />}
            />
          </section>

          {/* Email + Auth Providers (system-wide infra) moved to the
              super-admin console (/super-admin/system). The club admin
              console no longer renders them — the endpoints are super-admin
              only. */}

          <div className="admin-profile-brand-lower">
            <section
              id="settings-onboarding"
              data-testid="admin-settings-section-onboarding"
              className="admin-profile-access-card"
            >
              <header>
                <p className="eyebrow">Access</p>
                <h2>Onboarding &amp; downloads</h2>
              </header>
              <OnboardingCard settings={settingsQuery.data} />
              <DownloadsCard settings={settingsQuery.data} />
              <LiveSettingsPointer />
            </section>

            {canManageAdmins ? (
              <section
                id="settings-admins"
                data-testid="admin-settings-section-admins"
                className="admin-profile-admins-card"
              >
                <ClubAdminsCard currentUserId={me.data?.user?.id ?? null} />
              </section>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function LiveSettingsPointer() {
  return (
    <div
      data-testid="admin-settings-live-pointer"
      className="admin-profile-live-pointer"
    >
      <p className="text-ink">
        Authentication providers and live infrastructure are managed outside
        this page. Team stream keys and matchday readiness are on{" "}
        <a
          href="/admin/live"
          data-testid="admin-settings-live-link"
          className="text-accent hover:underline"
        >
          the Live page
        </a>
        .
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Onboarding controls
// ---------------------------------------------------------------------------

function OnboardingCard({ settings }: { settings: ClubSettingsPayload }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const live = (settings.settings ?? {}) as Record<string, unknown>;
  const selfServiceEnabled = coerceBool(live.onboarding_self_service_enabled ?? "1");
  const socialLoginEnabled = coerceBool(live.onboarding_social_login_enabled);
  const googleEnabled = coerceBool(live.onboarding_google_enabled ?? "1");
  const appleEnabled = coerceBool(live.onboarding_apple_enabled ?? "1");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (payload: UpdateClubSettingsRequest) =>
      updateClubSettings(payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: unscopedKey("club-settings"),
        }),
        queryClient.invalidateQueries({
          queryKey: publicAppSettingsKey(),
        }),
      ]);
      setError(null);
      toast.success("Onboarding settings saved");
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't save onboarding settings", {
        description: formatErr(err),
      });
    },
  });

  const toggle = (key: OnboardingSettingKey, current: boolean) => {
    setError(null);
    mut.mutate({ [key]: !current });
  };

  return (
    <section
      data-testid="admin-settings-onboarding"
      className="admin-profile-onboarding"
    >
      <div className="admin-profile-access-options">
        <OnboardingToggleRow
          title="Allow account registration and team-access requests"
          description="Allows new account creation and team access requests."
          enabled={selfServiceEnabled}
          disabled={mut.isPending}
          testId="admin-settings-onboarding-self-service"
          onToggle={() =>
            toggle("onboarding_self_service_enabled", selfServiceEnabled)
          }
        />
        <OnboardingToggleRow
          title="Allow available social sign-in"
          description="Allows Kratos OIDC sessions to exchange into Tktic app sessions."
          enabled={socialLoginEnabled}
          disabled={mut.isPending}
          testId="admin-settings-onboarding-social"
          onToggle={() =>
            toggle("onboarding_social_login_enabled", socialLoginEnabled)
          }
        />
        <OnboardingToggleRow
          title="Google sign-in"
          description="Shows the Google option when Kratos returns a configured Google provider."
          enabled={googleEnabled}
          disabled={mut.isPending || !socialLoginEnabled}
          testId="admin-settings-onboarding-google"
          onToggle={() => toggle("onboarding_google_enabled", googleEnabled)}
        />
        <OnboardingToggleRow
          title="Apple sign-in"
          description="Shows the Apple ID option when Kratos returns a configured Apple provider."
          enabled={appleEnabled}
          disabled={mut.isPending || !socialLoginEnabled}
          testId="admin-settings-onboarding-apple"
          onToggle={() => toggle("onboarding_apple_enabled", appleEnabled)}
        />
      </div>
      {error ? (
        <p
          data-testid="admin-settings-onboarding-error"
          className="text-destructive font-mono text-xs"
        >
          {error}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Club-admin co-admin delegation (role-model 2b). Lists the club's current
 * admins with a Demote action (the server's last-admin guard 409s if it would
 * strand the club — surfaced inline), an invite-by-email field for adding a
 * new admin, and a promote-existing-member picker (a `TkticSelect` of the
 * club's non-admin members from `GET /api/club/admins/candidates`, submitting
 * `POST /api/club/admins`). The picker is hidden when there are no promotable
 * members.
 */
function ClubAdminsCard({ currentUserId }: { currentUserId: string | null }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const adminsQuery = useQuery({
    queryKey: unscopedKey("club-admins"),
    queryFn: getHostClubAdmins,
  });
  const candidatesQuery = useQuery({
    queryKey: unscopedKey("club-admin-candidates"),
    queryFn: getHostClubAdminCandidates,
  });
  const [email, setEmail] = useState("");
  const [promoteId, setPromoteId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [demoteTarget, setDemoteTarget] = useState<{
    user_id: string;
    label: string;
  } | null>(null);

  const admins = adminsQuery.data?.admins ?? [];
  const candidates = candidatesQuery.data?.candidates ?? [];
  const isLastAdmin = admins.length <= 1;

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: unscopedKey("club-admins") }),
      queryClient.invalidateQueries({
        queryKey: unscopedKey("club-admin-candidates"),
      }),
    ]);

  const inviteMut = useMutation({
    mutationFn: () => inviteHostClubAdmin(email.trim()),
    onSuccess: async () => {
      setEmail("");
      setError(null);
      toast.success("Club-admin invite sent");
      await invalidate();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't send invite", { description: formatErr(err) });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (userId: string) => revokeHostClubAdmin(userId),
    onSuccess: async () => {
      setDemoteTarget(null);
      toast.success("Club admin stepped down");
      await invalidate();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 409
          ? "Can't step down the only club admin. Add another admin first."
          : formatErr(err);
      setDemoteTarget(null);
      toast.error("Couldn't update club admins", { description: msg });
    },
  });

  const grantMut = useMutation({
    mutationFn: (userId: string) => grantHostClubAdmin(userId),
    onSuccess: async () => {
      setPromoteId("");
      setError(null);
      toast.success("Member promoted to club admin");
      await invalidate();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't promote member", { description: formatErr(err) });
    },
  });

  return (
    <section
      data-testid="admin-settings-admins"
      className="admin-profile-admins"
    >
      <header>
        <p className="eyebrow">People who manage the club</p>
        <h2>Club admins</h2>
        <p className="mt-1 text-[0.65rem] text-ink-muted">
          People who administer this club. Invite another admin so you're not
          the only one — a club always keeps at least one.
        </p>
      </header>

      {adminsQuery.isLoading ? (
        <p className="font-mono text-[0.65rem] text-ink-muted">Loading admins…</p>
      ) : adminsQuery.error ? (
        <p
          data-testid="admin-settings-admins-error"
          className="font-mono text-xs text-destructive"
        >
          Failed to load club admins.
        </p>
      ) : (
        <ul data-testid="admin-settings-admins-list" className="flex flex-col gap-1">
          {admins.map((admin) => (
            <li
              key={admin.user_id}
              data-testid="admin-settings-admins-row"
              className="flex items-center justify-between gap-3 rounded border border-rule bg-elevated px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-xs text-ink">
                  {admin.display_name || admin.username}
                </p>
                <p className="truncate font-mono text-[0.6rem] text-ink-muted">
                  @{admin.username}
                  {admin.user_id === currentUserId ? " · you" : ""}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isLastAdmin || revokeMut.isPending}
                title={
                  isLastAdmin
                    ? "A club must keep at least one admin"
                    : undefined
                }
                onClick={() =>
                  setDemoteTarget({
                    user_id: admin.user_id,
                    label: admin.display_name || admin.username,
                  })
                }
                data-testid="admin-settings-admins-demote"
              >
                Step down
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex flex-col gap-2 border-t border-rule pt-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (!email.trim()) {
            setError("Enter an email to invite.");
            return;
          }
          inviteMut.mutate();
        }}
      >
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Invite a new club admin
          </span>
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              data-testid="admin-settings-admins-invite-email"
              className="min-w-0 flex-1 rounded border border-rule bg-canvas px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <Button
              type="submit"
              size="sm"
              disabled={inviteMut.isPending}
              data-testid="admin-settings-admins-invite-submit"
            >
              {inviteMut.isPending ? "Sending…" : "Send invite"}
            </Button>
          </div>
        </label>
        {error ? (
          <p
            data-testid="admin-settings-admins-invite-error"
            className="font-mono text-[0.65rem] text-destructive"
          >
            {error}
          </p>
        ) : null}
      </form>

      {/* Promote an existing member — the account already belongs to the club
          (from an invite / access request / direct create), so this grants
          club_admin without a new invite. Hidden when there's no one to
          promote. */}
      {candidates.length > 0 ? (
        <form
          className="flex flex-col gap-2 border-t border-rule pt-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (!promoteId) {
              setError("Pick a member to promote.");
              return;
            }
            grantMut.mutate(promoteId);
          }}
        >
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Promote an existing member
            </span>
            <div className="flex gap-2">
              <TkticSelect
                value={promoteId}
                onChange={setPromoteId}
                options={candidates.map((c) => ({
                  value: c.user_id,
                  label: `${c.display_name || c.username} (@${c.username})`,
                }))}
                placeholder="Pick a member…"
                ariaLabel="Member to promote to club admin"
                size="sm"
                data-testid="admin-settings-admins-promote-select"
              />
              <Button
                type="submit"
                size="sm"
                disabled={grantMut.isPending}
                data-testid="admin-settings-admins-promote-submit"
              >
                {grantMut.isPending ? "Promoting…" : "Promote"}
              </Button>
            </div>
          </label>
        </form>
      ) : null}

      {demoteTarget ? (
        <ConfirmDialog
          title="Step down club admin"
          message={
            <p className="text-xs text-ink-muted">
              Remove club-admin rights from{" "}
              <strong className="text-ink">{demoteTarget.label}</strong>? They
              stay a member of the club and can be re-promoted later.
            </p>
          }
          confirmLabel={revokeMut.isPending ? "Removing…" : "Step down"}
          confirmDisabled={revokeMut.isPending}
          danger
          onConfirm={() => revokeMut.mutate(demoteTarget.user_id)}
          onClose={() => setDemoteTarget(null)}
          data-testid="admin-settings-admins-demote-confirm"
        />
      ) : null}
    </section>
  );
}

function OnboardingToggleRow({
  title,
  description,
  enabled,
  disabled,
  testId,
  onToggle,
}: {
  title: string;
  description: string;
  enabled: boolean;
  disabled?: boolean;
  testId: string;
  onToggle: () => void;
}) {
  return (
    <div
      data-testid={testId}
      className="admin-profile-access-row"
    >
      <div className="min-w-0">
        <h4>
          {title}
        </h4>
        <p>
          {description}
        </p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        data-testid={`${testId}-toggle`}
        data-active={enabled ? "true" : undefined}
        className="admin-profile-switch"
        role="switch"
        aria-checked={enabled}
        aria-label={title}
      >
        <span aria-hidden />
        <span className="sr-only">{enabled ? "On" : "Off"}</span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// H1 — Branding card (+ H6 partial-save flow)
// ---------------------------------------------------------------------------

function BrandingCard({ settings }: { settings: ClubSettingsPayload }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const live = (settings.settings ?? {}) as Record<string, unknown>;
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const dirty = useMemo(
    () =>
      (Object.keys(draft) as BrandingSettingKey[]).filter(
        (k) => draft[k] !== (live[k] ?? "") && draft[k] !== undefined,
      ),
    [draft, live],
  );

  const save = useMutation({
    mutationFn: (payload: UpdateClubSettingsRequest) =>
      updateClubSettings(payload),
    onSuccess: async () => {
      // Invalidate both the club payload AND the public app-settings
      // cache so the public shell/header picks up branding changes
      // (e.g. `club_title`) immediately instead of waiting for the
      // 30 s staleTime to expire. Mirrors the wizard's invalidation
      // flow in WelcomeShell.TeamStep.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: unscopedKey("club-settings"),
        }),
        queryClient.invalidateQueries({
          queryKey: publicAppSettingsKey(),
        }),
      ]);
      setDraft({});
      setFieldErrors({});
      toast.success("Settings saved");
    },
    onError: (err) => {
      toast.error("Couldn't save settings", { description: formatErr(err) });
      if (err instanceof ApiError && err.status === 400) {
        // Per-key 400 from the backend's `errors` map.
        const detail = err.detail as unknown;
        if (
          detail &&
          typeof detail === "object" &&
          "errors" in (detail as Record<string, unknown>)
        ) {
          const errs = (detail as { errors: Record<string, string> }).errors;
          setFieldErrors(errs);
          setSubmitError("Some fields failed validation.");
          return;
        }
      }
      setSubmitError(formatErr(err));
    },
  });

  const grouped = useMemo(() => {
    const out: Record<string, typeof BRANDING_FIELDS> = {};
    for (const f of BRANDING_FIELDS) {
      (out[f.group] ||= []).push(f);
    }
    return out;
  }, []);

  return (
    <section
      data-testid="admin-settings-branding"
      className="admin-profile-identity"
    >
      <header className="admin-profile-identity-actions">
        <span>{dirty.length ? `${dirty.length} unsaved` : "Club details saved"}</span>
        <button
          type="button"
          onClick={() => {
            setSubmitError(null);
            setFieldErrors({});
            const payload = dirty.reduce<UpdateClubSettingsRequest>(
              (next, key) => ({ ...next, [key]: draft[key] }),
              {},
            );
            save.mutate(payload);
          }}
          disabled={dirty.length === 0 || save.isPending}
          data-testid="admin-settings-branding-save"
          className="admin-profile-save-details"
        >
          {save.isPending
            ? "Saving…"
            : "Save club details"}
        </button>
      </header>

      {submitError ? (
        <p
          data-testid="admin-settings-branding-error"
          className="text-destructive font-mono text-xs"
        >
          {submitError}
        </p>
      ) : null}

      {Object.entries(grouped).map(([group, fields]) => (
        <div key={group} className="flex flex-col gap-2">
          <div className="admin-profile-identity-fields">
            {fields.map((f) => (
              <BrandingField
                key={f.key}
                field={f}
                currentValue={
                  draft[f.key] !== undefined
                    ? draft[f.key]
                    : String(live[f.key] ?? "")
                }
                originalValue={String(live[f.key] ?? "")}
                error={fieldErrors[f.key]}
                onChange={(v) =>
                  setDraft((d) => ({ ...d, [f.key]: v }))
                }
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function BrandingField({
  field,
  currentValue,
  originalValue,
  error,
  onChange,
}: {
  field: (typeof BRANDING_FIELDS)[number];
  currentValue: string;
  originalValue: string;
  error: string | undefined;
  onChange: (v: string) => void;
}) {
  const dirty = currentValue !== originalValue;
  return (
    <label
      className="admin-profile-field"
      data-testid={`admin-settings-field-${field.key}`}
      data-dirty={dirty ? "true" : undefined}
    >
      <span>
        {field.label}{field.key === "club_title" ? " *" : ""}
      </span>
      {field.kind === "textarea" ? (
        <textarea
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          data-testid={`admin-settings-input-${field.key}`}
          className="admin-profile-input"
        />
      ) : (
        <input
          type="text"
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`admin-settings-input-${field.key}`}
          className="admin-profile-input"
        />
      )}
      {error ? (
        <span
          data-testid={`admin-settings-error-${field.key}`}
          className="text-destructive text-[0.6rem] font-mono"
        >
          {error}
        </span>
      ) : null}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Club colours — exact identity colours + scoped semantic previews
// ---------------------------------------------------------------------------

export function ClubColoursCard({
  settings,
  assetsSlot,
}: {
  settings: ClubSettingsPayload;
  assetsSlot?: ReactNode;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const live = (settings.settings ?? {}) as Record<string, unknown>;
  const initialPrimary = String(live.brand_primary_color ?? "");
  const initialSecondary = String(live.brand_secondary_color ?? "");
  const [savedColours, setSavedColours] = useState({
    primary: initialPrimary,
    secondary: initialSecondary,
  });
  const [primary, setPrimary] = useState(initialPrimary);
  const [secondary, setSecondary] = useState(initialSecondary);
  const [presetTarget, setPresetTarget] =
    useState<BrandColourField>("primary");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const clientErrors = useMemo(
    () => validateBrandColourDraft(primary, secondary),
    [primary, secondary],
  );
  const primaryCanonical = canonicalBrandHex(primary);
  const secondaryCanonical = canonicalBrandHex(secondary);
  const isDirty =
    primary !== savedColours.primary || secondary !== savedColours.secondary;
  const isValid = Object.keys(clientErrors).length === 0;
  const previewBundle = deriveBrandColorTokens(
    primaryCanonical,
    secondaryCanonical,
  );

  const clearErrors = () => {
    setSubmitError(null);
    // The backend validates the pair as one atomic state, so editing either
    // field can resolve an error associated with its sibling.
    setServerErrors({});
  };

  const setColour = (field: BrandColourField, value: string) => {
    clearErrors();
    if (field === "primary") setPrimary(value);
    else setSecondary(value);
  };

  const save = useMutation({
    mutationFn: (payload: UpdateClubSettingsRequest) =>
      updateClubSettings(payload),
    onSuccess: async (updated, submitted) => {
      const nextSettings = (updated.settings ?? {}) as Record<string, unknown>;
      const nextPrimary = String(
        nextSettings.brand_primary_color ?? submitted.brand_primary_color ?? "",
      );
      const nextSecondary = String(
        nextSettings.brand_secondary_color ??
          submitted.brand_secondary_color ??
          "",
      );
      setPrimary(nextPrimary);
      setSecondary(nextSecondary);
      setSavedColours({ primary: nextPrimary, secondary: nextSecondary });
      setSubmitError(null);
      setServerErrors({});
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: unscopedKey("club-settings"),
        }),
        queryClient.invalidateQueries({
          queryKey: publicAppSettingsKey(),
        }),
      ]);
      toast.success(
        nextPrimary ? "Club colours saved" : "Tktic colours restored",
      );
    },
    onError: (err) => {
      const errors = fieldErrorsFromApi(err);
      const message = Object.values(errors)[0] ?? formatErr(err);
      setServerErrors(errors);
      setSubmitError(
        Object.keys(errors).length > 0
          ? "Check the highlighted colour fields."
          : message,
      );
      toast.error("Couldn't save club colours", { description: message });
    },
  });

  const primaryError =
    clientErrors.brand_primary_color ?? serverErrors.brand_primary_color;
  const secondaryError =
    clientErrors.brand_secondary_color ?? serverErrors.brand_secondary_color;
  const activeValue = presetTarget === "primary" ? primary : secondary;
  const hasCustomDraft = Boolean(primaryCanonical);

  return (
    <section
      data-testid="admin-settings-club-colours"
      aria-busy={save.isPending}
      className="admin-profile-colours"
    >
      <div className="admin-profile-colour-fields">
        <BrandHexField
          field="primary"
          label="Primary colour *"
          value={primary}
          error={primaryError}
          disabled={save.isPending}
          help="Used for actions, selected tabs, links and the header tint."
          onFocus={() => setPresetTarget("primary")}
          onChange={(value) => setColour("primary", value)}
          onBlur={() => {
            if (primaryCanonical) setPrimary(primaryCanonical);
          }}
        />
        <BrandHexField
          field="secondary"
          label="Secondary colour"
          value={secondary}
          error={secondaryError}
          disabled={save.isPending}
          help="Optional. Used for selected header links and the club identity ribbon."
          action={
            <button
              type="button"
              onClick={() => setColour("secondary", "")}
              disabled={save.isPending || !secondary}
              data-testid="admin-settings-club-colours-clear-secondary"
              className="admin-profile-clear-secondary"
            >
              <X aria-hidden />
              Clear
            </button>
          }
          onFocus={() => setPresetTarget("secondary")}
          onChange={(value) => setColour("secondary", value)}
          onBlur={() => {
            if (secondaryCanonical) setSecondary(secondaryCanonical);
          }}
        />
      </div>

      <section className="admin-profile-colour-shortcuts">
        <header>
          <h3>Preset shortcuts</h3>
          <span
            data-testid="admin-settings-club-colours-status"
            className="admin-profile-theme-status"
          >
            {hasCustomDraft ? "Custom theme" : "Tktic default"}
            {isDirty ? " · unsaved" : ""}
          </span>
        </header>
        <div
          className="admin-profile-colour-target"
          role="group"
          aria-label="Choose which colour field presets update"
        >
          {(["primary", "secondary"] as const).map((field) => (
            <button
              key={field}
              type="button"
              aria-pressed={presetTarget === field}
              onClick={() => setPresetTarget(field)}
              disabled={save.isPending}
              data-testid={`admin-settings-club-colours-target-${field}`}
            >
              Apply to {field}
            </button>
          ))}
        </div>
        <div
          data-testid="admin-settings-club-colours-presets"
          className="admin-profile-colour-presets"
        >
          {BRAND_COLOUR_PRESETS.map((preset) => {
            const selected = activeValue.toUpperCase() === preset.hex;
            return (
              <button
                key={preset.hex}
                type="button"
                aria-label={`Use ${preset.name} for ${presetTarget}`}
                aria-pressed={selected}
                title={`${preset.name} · ${preset.hex}`}
                onClick={() => setColour(presetTarget, preset.hex)}
                disabled={save.isPending}
                data-testid={`admin-settings-club-colours-preset-${preset.name
                  .toLowerCase()
                  .replaceAll(" ", "-")}`}
              >
                <span aria-hidden style={{ backgroundColor: preset.hex }} />
                <span>{preset.name}</span>
                {selected ? <Check aria-hidden /> : null}
              </button>
            );
          })}
        </div>
      </section>

      {assetsSlot}

      {submitError ? (
        <p
          data-testid="admin-settings-club-colours-error"
          className="font-mono text-xs text-destructive"
          role="alert"
        >
          {submitError}
        </p>
      ) : null}

      <div className="admin-profile-colour-actions">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setPrimary("");
            setSecondary("");
            setPresetTarget("primary");
            setSubmitError(null);
            setServerErrors({});
          }}
          disabled={save.isPending || (!primary && !secondary)}
          data-testid="admin-settings-club-colours-restore"
        >
          <RotateCcw aria-hidden />
          Restore Tktic colours
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            if (!isValid) return;
            setSubmitError(null);
            setServerErrors({});
            save.mutate({
              brand_primary_color: primaryCanonical,
              brand_secondary_color: secondaryCanonical,
            });
          }}
          disabled={!isDirty || !isValid || save.isPending}
          data-testid="admin-settings-club-colours-save"
        >
          {save.isPending ? "Saving…" : "Save colours"}
        </Button>
      </div>

      <div className="admin-profile-preview-grid">
        <ClubColourPreview
          theme="light"
          bundle={previewBundle}
          clubName={String(live.club_title ?? "Your club")}
          clubIntro={String(live.season_intro ?? "")}
          logoUrl={getUploadedClubLogoUrl({ settings: { club_logo_filename: String(live.club_logo_filename ?? "") }, assets: settings.assets })}
        />
        <ClubColourPreview
          theme="dark"
          bundle={previewBundle}
          clubName={String(live.club_title ?? "Your club")}
          clubIntro={String(live.season_intro ?? "")}
          logoUrl={getUploadedClubLogoUrl({ settings: { club_logo_filename: String(live.club_logo_filename ?? "") }, assets: settings.assets })}
        />
      </div>
      <p className="admin-profile-preview-note">
        Preview only. Draft colours stay inside these club specimens until you
        save. The Tktic product mark keeps its original colour.
      </p>

    </section>
  );
}

function BrandHexField({
  field,
  label,
  value,
  error,
  disabled,
  help,
  action,
  onFocus,
  onChange,
  onBlur,
}: {
  field: BrandColourField;
  label: string;
  value: string;
  error?: string;
  disabled: boolean;
  help: string;
  action?: ReactNode;
  onFocus: () => void;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  const inputId = `admin-settings-club-colours-${field}`;
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;
  return (
    <div className="admin-profile-field admin-profile-colour-field">
      <div className="admin-profile-field-heading">
        <label
          htmlFor={inputId}
        >
          {label}
        </label>
        {action}
      </div>
      <label className="admin-profile-colour-input" htmlFor={inputId}>
        <span
          aria-hidden
          className="admin-profile-colour-swatch"
          style={
            BRAND_HEX_PATTERN.test(value)
              ? { backgroundColor: value }
              : undefined
          }
        />
        <input
          id={inputId}
          type="text"
          value={value}
          placeholder="#2563EB"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={`${helpId}${error ? ` ${errorId}` : ""}`}
          onFocus={onFocus}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          data-testid={`admin-settings-club-colours-${field}-input`}
          className="admin-profile-input"
        />
      </label>
      <p id={helpId} className="admin-profile-field-help">
        {help}
      </p>
      {error ? (
        <p
          id={errorId}
          data-testid={`admin-settings-club-colours-${field}-error`}
          className="font-mono text-[0.6rem] text-destructive"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

type BrandPreviewBundle = NonNullable<
  ReturnType<typeof deriveBrandColorTokens>
>;

function ClubColourPreview({
  theme,
  bundle,
  clubName,
  clubIntro,
  logoUrl,
}: {
  theme: "light" | "dark";
  bundle: BrandPreviewBundle | null;
  clubName: string;
  clubIntro: string;
  logoUrl: string | null;
}) {
  const tokens = bundle?.[theme];
  const style = tokens
    ? ({
        "--accent": tokens.accent,
        "--accent-rgb": tokens.accentRgb,
        "--accent-hover": tokens.accentHover,
        "--accent-hover-rgb": tokens.accentHoverRgb,
        "--accent-ink": tokens.accentInk,
        "--accent-ink-rgb": tokens.accentInkRgb,
        "--accent-text": tokens.accentText,
        "--accent-text-rgb": tokens.accentTextRgb,
        "--focus-ring": tokens.focusRing,
        "--focus-ring-rgb": tokens.focusRingRgb,
        "--accent-bg": tokens.accent,
        "--accent-foreground": tokens.accentInk,
        "--ring": tokens.focusRing,
      } as CSSProperties)
    : undefined;

  return (
    <article
      data-theme={theme}
      data-club-brand-scope="static"
      data-testid={`admin-settings-club-colours-preview-${theme}`}
      aria-label={`${theme === "light" ? "Light" : "Dark"} theme preview`}
      className="admin-profile-theme-preview"
      style={style}
    >
      {bundle ? (
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 flex h-[3px]"
          data-testid={`admin-settings-club-colours-preview-${theme}-ribbon`}
        >
          <span
            className="h-full flex-1"
            style={{ backgroundColor: bundle.primary }}
          />
          {bundle.secondary ? (
            <span
              className="h-full flex-1"
              style={{ backgroundColor: bundle.secondary }}
            />
          ) : null}
        </div>
      ) : null}
      <div className="admin-profile-theme-preview-body">
        <p className="eyebrow">{theme} preview</p>
        <ClubAssetMark
          kind="club_logo"
          clubName={clubName}
          imageUrl={logoUrl}
          compact
        />
        <h3>{clubName || "Your club"}</h3>
        <p>{clubIntro || "A place to learn, play and grow together."}</p>
        <span className="admin-profile-preview-action">View teams</span>
      </div>
    </article>
  );
}

function canonicalBrandHex(value: string): string {
  return BRAND_HEX_PATTERN.test(value) ? value.toUpperCase() : "";
}

function validateBrandColourDraft(
  primary: string,
  secondary: string,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (primary && !BRAND_HEX_PATTERN.test(primary)) {
    errors.brand_primary_color = "Enter a six-digit HEX value such as #2563EB.";
  }
  if (secondary && !BRAND_HEX_PATTERN.test(secondary)) {
    errors.brand_secondary_color =
      "Enter a six-digit HEX value such as #D97706.";
  }
  if (!primary && secondary) {
    errors.brand_primary_color = "Choose a primary colour before adding a secondary.";
    errors.brand_secondary_color ??=
      "Secondary colour requires a primary colour.";
  }
  return errors;
}

// ---------------------------------------------------------------------------
// H3 — Downloads toggle
// ---------------------------------------------------------------------------

function DownloadsCard({ settings }: { settings: ClubSettingsPayload }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const live = (settings.settings ?? {}) as Record<string, unknown>;
  const currentEnabled = coerceBool(live.downloads_enabled);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (next: boolean) =>
      updateClubSettings({ downloads_enabled: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("club-settings"),
      });
      toast.success("Settings saved");
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't save settings", { description: formatErr(err) });
    },
  });

  return (
    <section
      data-testid="admin-settings-downloads"
      className="admin-profile-downloads"
    >
      <div>
        <h3>Allow match downloads</h3>
        <p>Members with access may download available match recordings.</p>
      </div>
      <button
        type="button"
        onClick={() => {
          setError(null);
          mut.mutate(!currentEnabled);
        }}
        disabled={mut.isPending}
        data-testid="admin-settings-downloads-toggle"
        data-active={currentEnabled ? "true" : undefined}
        className="admin-profile-switch"
        role="switch"
        aria-checked={currentEnabled}
        aria-label="Allow match downloads"
      >
        <span aria-hidden />
        <span className="sr-only">
          {currentEnabled ? "Downloads enabled" : "Downloads disabled"}
        </span>
      </button>
      {error ? (
        <p
          data-testid="admin-settings-downloads-error"
          className="text-destructive font-mono text-xs"
        >
          {error}
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// H4 — Asset uploads
// ---------------------------------------------------------------------------

function AssetsCard({ settings }: { settings: ClubSettingsPayload }) {
  const live = (settings.settings ?? {}) as Record<string, unknown>;
  const clubName = String(live.club_title ?? "Your club");
  return (
    <section
      data-testid="admin-settings-assets"
      className="admin-profile-assets"
    >
      <div>
        <AssetRow
          kind="club_logo"
          label="Club logo"
          hint="Used in the app header and branded emails."
          previewUrl={getUploadedClubLogoUrl({ settings: { club_logo_filename: String(live.club_logo_filename ?? "") }, assets: settings.assets })}
          clubName={clubName}
        />
        <AssetRow
          kind="favicon"
          label="Browser icon"
          hint="Uses the club logo until you upload a separate icon."
          previewUrl={settings.assets?.favicon_url ?? null}
          clubName={clubName}
        />
      </div>
    </section>
  );
}

function AssetRow({
  kind,
  label,
  hint,
  previewUrl,
  clubName,
}: {
  kind: "club_logo" | "favicon";
  label: string;
  hint?: string;
  previewUrl: string | null;
  clubName: string;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [localPreview, setLocalPreview] = useState<{
    url: string;
    fileName: string;
  } | null>(null);
  const [imageAvailable, setImageAvailable] = useState(Boolean(previewUrl));
  const localPreviewRef = useRef<string | null>(null);

  useEffect(() => setImageAvailable(Boolean(previewUrl)), [previewUrl]);

  useEffect(
    () => () => {
      if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
    },
    [],
  );

  const mut = useMutation({
    mutationFn: (file: File) => uploadClubSettingsAsset(kind, file),
    onSuccess: async () => {
      // The public app-settings cache drives the live favicon/title sync
      // (useBrandSync), so refresh it alongside the club payload.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: unscopedKey("club-settings") }),
        queryClient.invalidateQueries({ queryKey: publicAppSettingsKey() }),
      ]);
      if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
      localPreviewRef.current = null;
      setLocalPreview(null);
      toast.success(`${label} uploaded`);
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error(`Couldn't upload ${label.toLowerCase()}`, {
        description: formatErr(err),
      });
    },
  });

  return (
    <div
      data-testid={`admin-settings-asset-${kind}`}
      className="admin-profile-asset-row"
    >
      <div className="admin-profile-asset-label">
        <span>{label}</span>
        {hint ? <small>{hint}</small> : null}
      </div>
      <div className="admin-profile-asset-control">
        <ClubAssetMark
          kind={kind}
          clubName={clubName}
          imageUrl={localPreview?.url ?? previewUrl}
          testId={`admin-settings-asset-${kind}-preview`}
          onImageAvailabilityChange={setImageAvailable}
        />
        <label className="admin-profile-file-button">
          <ImageUp aria-hidden />
          <span>Choose file</span>
          <input
            type="file"
            accept={kind === "favicon" ? ".ico,.png,.svg" : ".png,.jpg,.jpeg,.svg,.webp"}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (localPreviewRef.current) {
                URL.revokeObjectURL(localPreviewRef.current);
              }
              const url = URL.createObjectURL(file);
              localPreviewRef.current = url;
              setLocalPreview({ url, fileName: file.name });
              setError(null);
              mut.mutate(file);
              e.target.value = "";
            }}
            disabled={mut.isPending}
            data-testid={`admin-settings-asset-${kind}-input`}
          />
        </label>
        <span className="admin-profile-asset-state">
          {localPreview
            ? `${mut.isPending ? "Uploading" : "Selected"}: ${localPreview.fileName}`
            : previewUrl && imageAvailable
              ? `Saved ${label.toLowerCase()}`
              : kind === "favicon"
                ? "Using default club icon"
                : "Using default club logo"}
        </span>
        {mut.isPending ? <span className="admin-profile-uploading">Uploading…</span> : null}
      </div>
      {error ? (
        <p
          data-testid={`admin-settings-asset-${kind}-error`}
          className="text-destructive font-mono text-[0.6rem]"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ClubAssetMark({
  kind,
  clubName,
  imageUrl,
  compact = false,
  testId,
  onImageAvailabilityChange,
}: {
  kind: "club_logo" | "favicon";
  clubName: string;
  imageUrl: string | null;
  compact?: boolean;
  testId?: string;
  onImageAvailabilityChange?: (available: boolean) => void;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
    onImageAvailabilityChange?.(Boolean(imageUrl));
  }, [imageUrl, onImageAvailabilityChange]);
  const showImage = Boolean(imageUrl) && !imageFailed;
  return (
    <span
      className="club-asset-mark admin-profile-club-mark"
      data-kind={kind}
      data-compact={compact ? "true" : undefined}
      data-has-image={showImage ? "true" : "false"}
      data-testid={testId ?? undefined}
      aria-label={showImage ? `Saved ${kind === "favicon" ? "browser icon" : "club logo"}` : `Default ${kind === "favicon" ? "club icon" : "club logo"}`}
    >
      {showImage ? (
        <img
          src={imageUrl ?? undefined}
          alt=""
          onLoad={() => onImageAvailabilityChange?.(true)}
          onError={() => {
            setImageFailed(true);
            onImageAvailabilityChange?.(false);
          }}
        />
      ) : (
        <ClubCrest name={clubName} initials={kind === "favicon" ? 1 : 2} />
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

// coerceBool moved to `web/src/lib/coerce.ts` (M11 lift — public viewer + admin share).

function fieldErrorsFromApi(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || (err.status !== 400 && err.status !== 422)) {
    return {};
  }
  const detail = err.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const errors = (detail as { errors?: unknown }).errors;
    if (errors && typeof errors === "object" && !Array.isArray(errors)) {
      return Object.fromEntries(
        Object.entries(errors).filter(
          (entry): entry is [string, string] => typeof entry[1] === "string",
        ),
      );
    }
  }
  if (!Array.isArray(detail)) return {};
  const errors: Record<string, string> = {};
  for (const item of detail) {
    if (!item || typeof item !== "object") continue;
    const loc = (item as { loc?: unknown }).loc;
    const message = (item as { msg?: unknown }).msg;
    if (!Array.isArray(loc) || typeof message !== "string") continue;
    const field = loc.at(-1);
    if (typeof field === "string") errors[field] = message;
  }
  return errors;
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.detail && typeof err.detail === "object" && "message" in err.detail) {
      const msg = (err.detail as { message?: unknown }).message;
      if (typeof msg === "string") return msg;
    }
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}
